import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import EditableDetailsItem from './EditableDetailsItem';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useUpdateEntity } from '../../../api/bitedb/entities/entities.hooks';
import { useUpdateProduct } from '../../../api/bitedb/products/products.hooks';
import { enqueueSnackbar } from 'notistack';

interface DashboardProps {
  supplier?: any;
  onEditStateChange?: any;
  onSavingChange?: any;
  hideHeader?: boolean;
}

export interface DetailsCardHandles {
  startEdit: () => void;
  save: () => Promise<void>;
  cancel: () => void;
  isEditing: () => boolean;
}

const DetailsCard = forwardRef<DetailsCardHandles, DashboardProps>(
  (
    { supplier, onEditStateChange, onSavingChange, hideHeader = false },
    ref,
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState<any>({});

    // Determine if this is a product based on multiple factors
    const isProduct =
      supplier?.type === 'products' ||
      supplier?.manufacturer !== undefined ||
      supplier?.vendor !== undefined ||
      supplier?.model_number !== undefined ||
      supplier?.specifications !== undefined ||
      window.location.pathname.includes('product');

    // Get the appropriate update mutation
    const updateEntityMutation = useUpdateEntity(supplier?.database_id);
    const updateProductMutation = useUpdateProduct();

    const handleEdit = useCallback(() => {
      setIsEditing(true);
      onEditStateChange?.(true);
      // Initialize edited data with current values - include ALL fields
      setEditedData({
        name: supplier?.name || '',
        category: supplier?.category || '',
        ...(isProduct
          ? {
              manufacturer: supplier?.manufacturer || '',
              vendor: supplier?.vendor || '',
              model_number: supplier?.model_number || '',
              specifications: supplier?.specifications || '',
              hts_code: supplier?.hts_code || '',
              hierarchyType: supplier?.hierarchy_type || '',
            }
          : {
              email: supplier?.email || '',
              phone: supplier?.phone || '',
              address: supplier?.address || '',
              address1: supplier?.address1 || '',
              address2: supplier?.address2 || '',
              alias: supplier?.alias || '',
              domain: supplier?.domain || '',
              business_registration_number:
                supplier?.business_registration_number || '',
              person_id: supplier?.person_id || '',
              manufacturer_identification_number:
                supplier?.manufacturer_identification_number || '',
            }),
        country_of_origin: supplier?.country_of_origin || '',
      });
    }, [isProduct, onEditStateChange, supplier]);

    const handleCancel = useCallback(() => {
      setIsEditing(false);
      setEditedData({});
      onEditStateChange?.(false);
    }, [onEditStateChange]);

    const handleSave = useCallback(async () => {
      let savingStarted = false;
      try {
        const getCurrentFieldValue = (key: string) => supplier?.[key] ?? '';
        const getEditedFieldValue = (key: string) =>
          Object.prototype.hasOwnProperty.call(editedData, key)
            ? editedData[key]
            : supplier?.[key] ?? '';
        const normalizeNullableText = (value: any) => {
          if (typeof value !== 'string') {
            return value ?? null;
          }
          const trimmed = value.trim();
          return trimmed === '' ? null : trimmed;
        };

        // Check if there are any changes
        let hasChanges = false;
        Object.keys(editedData).forEach((key) => {
          const currentValue = getCurrentFieldValue(key);
          const editedValue = getEditedFieldValue(key);
          if (editedValue !== currentValue) {
            hasChanges = true;
          }
        });

        // Only proceed if there are changes
        if (!hasChanges) {
          enqueueSnackbar('No changes to save', { variant: 'info' });
          setIsEditing(false);
          setEditedData({});
          onEditStateChange?.(false);
          return;
        }

        onSavingChange?.(true);
        savingStarted = true;

        // Prepare full data object with all fields (current + edited)
        const fullData: any = {
          name: getEditedFieldValue('name'),
          category: getEditedFieldValue('category'),
          country_of_origin: getEditedFieldValue('country_of_origin'),
        };

        if (isProduct) {
          // Add product-specific fields
          fullData.manufacturer = getEditedFieldValue('manufacturer');
          fullData.model_number = getEditedFieldValue('model_number');
          fullData.specifications = getEditedFieldValue('specifications');
          fullData.hts_code = getEditedFieldValue('hts_code');
          fullData.vendor = getCurrentFieldValue('vendor');
          fullData.status = supplier?.status || '';
        } else {
          // Add entity-specific fields
          fullData.email = getEditedFieldValue('email');
          fullData.phone = getEditedFieldValue('phone');
          fullData.address = getEditedFieldValue('address');
          fullData.address1 = normalizeNullableText(getEditedFieldValue('address1'));
          fullData.address2 = normalizeNullableText(getEditedFieldValue('address2'));
          fullData.alias = normalizeNullableText(getEditedFieldValue('alias'));
          fullData.domain = normalizeNullableText(getEditedFieldValue('domain'));
          fullData.description = supplier?.description || '';
          fullData.subcategory = supplier?.subcategory || '';
          fullData.risk_level = supplier?.risk_level || '';
          fullData.business_unit = supplier?.business_unit || '';
          fullData.parent_business = supplier?.parent_business || '';
          fullData.sku_product_id = supplier?.sku_product_id || '';
          fullData.supplier_vendor_id = supplier?.supplier_vendor_id || '';
          fullData.business_registration_number = normalizeNullableText(
            getEditedFieldValue('business_registration_number'),
          );
          fullData.person_id = normalizeNullableText(getEditedFieldValue('person_id'));
          fullData.manufacturer_identification_number =
            normalizeNullableText(getEditedFieldValue('manufacturer_identification_number'));
        }

        let result = null;
        if (isProduct) {
          result = await updateProductMutation.mutateAsync({
            id: supplier?.id,
            productData: fullData,
          });
        } else {
          result = await updateEntityMutation.mutateAsync({
            id: supplier?.entity_id || supplier?.id,
            entityData: fullData,
          });
        }

        if (result?.success) {
          enqueueSnackbar('Details updated successfully', {
            variant: 'success',
          });
          setIsEditing(false);
          setEditedData({});
          onEditStateChange?.(false);
        } else {
          enqueueSnackbar(result?.message || 'Failed to update details', {
            variant: 'error',
          });
        }
      } catch (error) {
        console.error('Error updating details:', error);
        enqueueSnackbar('Error updating details', { variant: 'error' });
      } finally {
        if (savingStarted) {
          onSavingChange?.(false);
        }
      }
    }, [
      editedData,
      isProduct,
      onEditStateChange,
      onSavingChange,
      supplier,
      updateEntityMutation,
      updateProductMutation,
    ]);

    const handleFieldChange = (field: string, value: string) => {
      setEditedData((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    };

    useImperativeHandle(
      ref,
      () => ({
        startEdit: handleEdit,
        save: handleSave,
        cancel: handleCancel,
        isEditing: () => isEditing,
      }),
      [handleCancel, handleEdit, handleSave, isEditing],
    );

    if (!supplier) {
      return (
        <div className='screening-card'>
          {!hideHeader && (
            <div className='screening-card-header'>
              <div className='title-group'>
                <h3>Entity Details</h3>
              </div>
            </div>
          )}
          <p className='subtitle'>Item attributes and properties</p>
          <div
            style={{ padding: '20px', textAlign: 'center', color: '#FFFFFF' }}
          >
            No details available for this item.
          </div>
        </div>
      );
    }

    return (
      <div
        className='screening-card'
        style={{ overflow: 'auto', maxWidth: '100%' }}
      >
        {!hideHeader && (
          <div className='screening-card-header'>
            <div className='title-group'>
              <h3>Entity Details</h3>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#4895ef',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title='Edit details'
                >
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </button>
              ) : onEditStateChange ? (
                <span
                  className='hit-meta'
                  style={{
                    color: 'var(--text-primary)',
                    fontSize: '12px',
                  }}
                >
                  Editing
                </span>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={
                      updateEntityMutation.isPending ||
                      updateProductMutation.isPending
                    }
                    className='btn btn-primary'
                    title='Save changes'
                  >
                    <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                  </button>
                  <button
                    onClick={handleCancel}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#f44336',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title='Cancel editing'
                  >
                    <CloseOutlinedIcon sx={{ fontSize: 18 }} />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        <p className='subtitle'>Item attributes and properties</p>

        <div className='relationship-details-grid'>
          {[
            {
              key: 'name',
              label: 'Name',
              value: isEditing ? editedData.name : supplier.name,
              type: 'text' as const,
            },
            {
              key: 'category',
              label: 'Category',
              value: isEditing ? editedData.category : supplier.category,
              type: 'text' as const,
            },
            ...(isProduct
              ? [
                  {
                    key: 'manufacturer',
                    label: 'Manufacturer',
                    value: isEditing
                      ? editedData.manufacturer
                      : supplier.manufacturer,
                    type: 'text' as const,
                  },
                  {
                    key: 'vendor',
                    label: 'Vendor',
                    value: isEditing ? editedData.vendor : supplier.vendor,
                    type: 'text' as const,
                  },
                  {
                    key: 'model_number',
                    label: 'Model Number',
                    value: isEditing
                      ? editedData.model_number
                      : supplier.model_number,
                    type: 'text' as const,
                  },
                  {
                    key: 'specifications',
                    label: 'Specifications',
                    value: isEditing
                      ? editedData.specifications
                      : supplier.specifications,
                    type: 'text' as const,
                  },
                  {
                    key: 'hts_code',
                    label: 'HTS Code',
                    value: isEditing ? editedData.hts_code : supplier.hts_code,
                    type: 'text' as const,
                  },
                ]
              : [
                  {
                    key: 'alias',
                    label: 'Alias',
                    value: isEditing ? editedData.alias : supplier.alias,
                    type: 'text' as const,
                  },
                  {
                    key: 'email',
                    label: 'Contact Email',
                    value: isEditing ? editedData.email : supplier.email,
                    type: 'email' as const,
                  },
                  {
                    key: 'phone',
                    label: 'Contact Phone',
                    value: isEditing ? editedData.phone : supplier.phone,
                    type: 'tel' as const,
                  },
                  {
                    key: 'address',
                    label: 'Address',
                    value: isEditing ? editedData.address : supplier.address,
                    type: 'text' as const,
                  },
                  {
                    key: 'address1',
                    label: 'Address 1',
                    value: isEditing ? editedData.address1 : supplier.address1,
                    type: 'text' as const,
                  },
                  {
                    key: 'address2',
                    label: 'Address 2',
                    value: isEditing ? editedData.address2 : supplier.address2,
                    type: 'text' as const,
                  },
                  {
                    key: 'domain',
                    label: 'Domain',
                    value: isEditing ? editedData.domain : supplier.domain,
                    type: 'text' as const,
                  },
                  {
                    key: 'business_registration_number',
                    label: 'Business Registration Number / (EIN)',
                    value: isEditing
                      ? editedData.business_registration_number
                      : supplier.business_registration_number,
                    type: 'text' as const,
                  },
                  {
                    key: 'person_id',
                    label: 'Person ID',
                    value: isEditing
                      ? editedData.person_id
                      : supplier.person_id,
                    type: 'text' as const,
                  },
                  {
                    key: 'manufacturer_identification_number',
                    label: 'Manufacturer Identification Number (MID)',
                    value: isEditing
                      ? editedData.manufacturer_identification_number
                      : supplier.manufacturer_identification_number,
                    type: 'text' as const,
                  },
                ]),
            {
              key: 'country_of_origin',
              label: 'Entity Country',
              value: isEditing
                ? editedData.country_of_origin
                : supplier.country_of_origin,
              type: 'text' as const,
            },
          ].map((item) => (
            <div key={item.key} className='relationship-details-grid-item'>
              <EditableDetailsItem
                label={item.label}
                fieldKey={item.key}
                value={item.value}
                isEditing={isEditing}
                onChange={(value) => handleFieldChange(item.key, value)}
                type={item.type}
                isReadOnly={Boolean(
                  (item as { isReadOnly?: boolean }).isReadOnly,
                )}
              />
            </div>
          ))}
        </div>
      </div>
    );
  },
);

DetailsCard.displayName = 'DetailsCard';

export default DetailsCard;
